export const LINEUP_POSITIONS = ['Sturm', 'Verteidigung', 'Deckel'];
export const TEAM_COLORS = { blue: '#0D47A1', white: '#78909C' };
export const TEAM_LABELS = { blue: 'Team Blau', white: 'Team Weiß' };
export const TOURNAMENT_MAX_PER_POS = 6;
export const TOURNAMENT_MAX_GAMES = 10;

// slots: fixed roles by index (null = no fixed assignment)
export const SWAP_MODES = {
  solo: { label: 'Durchspielen', size: 1, slots: ['Durch'] },
  '1:1': { label: '1:1 Wechsel', size: 2, slots: ['Wasser', 'Wechselbank'] },
  '2:1': { label: '3er (2 Wasser, 1 Bank)', size: 3, slots: ['Wasser', 'Wasser', 'Wechselbank'] },
  '1:2': { label: '3er (1 Wasser, 2 Bank)', size: 3, slots: ['Wasser', 'Wechselbank', 'Wechselbank'] },
  '2:2': { label: '4er (2 Wasser, 2 Bank)', size: 4, slots: null },
};
export const SWAP_MODE_KEYS = Object.keys(SWAP_MODES);
export const SWAP_GROUP_COLORS = ['#C62828', '#2E7D32', '#EF6C00', '#6A1B9A', '#00838F', '#AD1457'];

export function groupLetter(index) {
  return String.fromCharCode(65 + (index % 26));
}

export function groupColor(index) {
  return SWAP_GROUP_COLORS[index % SWAP_GROUP_COLORS.length];
}

export function newGroupId(pos) {
  return `${pos}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

// Groups of a position, restricted to players that are still in the position list.
export function getSwapGroups(game, pos) {
  const inPos = new Set(game?.[pos] || []);
  return (game?.swapGroups?.[pos] || []).map(group => ({
    ...group,
    players: (group.players || []).filter(id => inPos.has(id)),
  }));
}

// Each role caption is shown once, above the first slot of that role.
export function slotCaption(mode, index) {
  const slots = SWAP_MODES[mode]?.slots;
  if (!slots || !slots[index]) return null;
  return index === 0 || slots[index] !== slots[index - 1] ? slots[index] : null;
}

// Index of the single player with the opposite role, or null if there is none or it is ambiguous.
export function getSwapPartnerIndex(mode, playerCount, index) {
  const slots = SWAP_MODES[mode]?.slots;
  if (!slots || slots[index] === 'Durch') return null;
  const others = [];
  for (let k = 0; k < playerCount; k += 1) {
    if (slots[k] !== slots[index]) others.push(k);
  }
  return others.length === 1 ? others[0] : null;
}

export function getUngroupedPlayers(game, pos) {
  const grouped = new Set(getSwapGroups(game, pos).flatMap(group => group.players));
  return (game?.[pos] || []).filter(id => !grouped.has(id));
}
export const LINEUP_META_KEY = '__meta';

const LINEUP_CREATION_MODES = new Set(['auto', 'manual']);

export function emptyGame(idx) {
  return {
    name: `Spiel ${idx + 1}`,
    Sturm: [], Verteidigung: [], Deckel: [],
    swapGroups: { Sturm: [], Verteidigung: [], Deckel: [] },
  };
}

export function emptyTrainingLineup() {
  return {
    type: 'training',
    teamBlue:  { Sturm: [], Verteidigung: [], Deckel: [] },
    teamWhite: { Sturm: [], Verteidigung: [], Deckel: [] },
  };
}

export function emptyTournamentLineup() {
  return { type: 'turnier', games: [emptyGame(0)] };
}

export function sanitizeLineupMeta(meta) {
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return null;

  const creationMode = LINEUP_CREATION_MODES.has(meta.creationMode)
    ? meta.creationMode
    : null;
  const usesHistory = meta.usesHistory === true;
  const manualOverride = meta.manualOverride === true;
  const historySamples = Number.isInteger(meta.historySamples) && meta.historySamples > 0
    ? meta.historySamples
    : 0;

  const sanitized = {
    creationMode: creationMode || (manualOverride ? 'manual' : 'auto'),
    usesHistory,
    manualOverride,
  };

  if (historySamples > 0) sanitized.historySamples = historySamples;

  ['createdAt', 'updatedAt', 'manualUpdatedAt'].forEach(key => {
    const value = typeof meta[key] === 'string' && meta[key].trim()
      ? meta[key].trim()
      : '';
    if (value) sanitized[key] = value;
  });

  return sanitized;
}

export function getLineupMeta(lineup) {
  if (!lineup || typeof lineup !== 'object' || Array.isArray(lineup)) return null;
  return sanitizeLineupMeta(lineup[LINEUP_META_KEY]);
}

export function withLineupMeta(lineup, meta) {
  if (!lineup || typeof lineup !== 'object' || Array.isArray(lineup)) return lineup;
  const sanitizedMeta = sanitizeLineupMeta(meta);
  if (!sanitizedMeta) return { ...lineup };
  return { ...lineup, [LINEUP_META_KEY]: sanitizedMeta };
}

export function hasLineupAssignments(lineup) {
  if (!lineup || typeof lineup !== 'object') return false;
  if (lineup.type === 'training') {
    return ['teamBlue', 'teamWhite'].some(team =>
      LINEUP_POSITIONS.some(pos => Array.isArray(lineup?.[team]?.[pos]) && lineup[team][pos].length > 0)
    );
  }
  const games = Array.isArray(lineup.games) ? lineup.games : [];
  return games.some(g =>
    LINEUP_POSITIONS.some(pos => Array.isArray(g?.[pos]) && g[pos].length > 0) ||
    (Array.isArray(g?.reserve) && g.reserve.length > 0)
  );
}
